import { useEffect, useState } from 'react';
import { MailCheck, ShieldCheck, CircleAlert } from 'lucide-react';
import { OtpInput, useToast } from '@kollez/design-system';
import { apiPost } from '@/lib/api';
import { useAuthStore, type Membership, type AuthUser } from '@/lib/authStore';

/**
 * ClaimOtpsStep (PR 4b, Step 5 — Path 3 only) — dual-channel OTP verify for roster match.
 *
 * The server (PR 4a) sends OTPs to the ROSTER's email + mobile (NOT the student's typed
 * contacts). This step verifies both. On success: shell claimed atomically, session hydrated,
 * caller navigates to /home. On failure: user can retry or fall back to Path 2 with the
 * onFallback callback.
 */

interface ClaimOtpsStepProps {
  claimAttemptId: string;
  userId: string;
  expiresInSec: number;
  otpsForDev?: { emailOtp?: string; mobileOtp?: string };
  onSuccess: () => void;
  onFallbackToPath2: () => void;
}

interface VerifyResponse {
  user: AuthUser;
  memberships: Membership[];
  activeTenantId: string | null;
  tokens: { accessToken: string; refreshToken: string; expiresIn: number };
}

export function ClaimOtpsStep({
  claimAttemptId,
  userId,
  expiresInSec,
  otpsForDev,
  onSuccess,
  onFallbackToPath2,
}: ClaimOtpsStepProps) {
  const toast = useToast();
  const setSession = useAuthStore((s) => s.setSession);

  const [emailOtp, setEmailOtp] = useState<string[]>(
    otpsForDev?.emailOtp ? otpsForDev.emailOtp.split('') : ['', '', '', '', '', ''],
  );
  const [mobileOtp, setMobileOtp] = useState<string[]>(
    otpsForDev?.mobileOtp ? otpsForDev.mobileOtp.split('') : ['', '', '', '', '', ''],
  );
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [remaining, setRemaining] = useState(expiresInSec);

  useEffect(() => {
    if (remaining <= 0) return;
    const t = setInterval(() => setRemaining((r) => Math.max(0, r - 1)), 1000);
    return () => clearInterval(t);
  }, [remaining]);

  async function verify() {
    setSubmitting(true);
    setError(null);
    try {
      const emailStr = emailOtp.join('');
      const mobileStr = mobileOtp.join('');
      if (emailStr.length !== 6 || mobileStr.length !== 6) {
        setError('Both OTPs must be 6 digits.');
        return;
      }
      const res = await apiPost<VerifyResponse>('/auth/register/verify-claim-otps', {
        claimAttemptId,
        emailOtp: emailStr,
        mobileOtp: mobileStr,
        userId,
      });
      setSession(res.user, res.tokens, {
        activeTenantId: res.activeTenantId,
        memberships: res.memberships,
      });
      toast.show({
        title: 'Verified',
        message: 'Your college has been added to your workspaces.',
      });
      onSuccess();
    } catch (err) {
      const msg =
        (err as { response?: { data?: { error?: { message?: string } } } }).response?.data?.error?.message
        ?? 'Verification failed. Please check the codes and try again.';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  }

  const min = Math.floor(remaining / 60);
  const sec = remaining % 60;
  const expired = remaining <= 0;

  return (
    <section className="grid gap-5">
      <div>
        <MailCheck size={26} className="text-ink-accent" />
        <h1 className="h-page mt-4">Verify your college roster</h1>
        <p className="subhead mt-2">
          We&apos;ve sent two 6-digit codes — one to the email and one to the mobile your college
          has on file. Enter both to claim your profile.
        </p>
        {!expired ? (
          <p className="helper mt-2 m-0">
            Codes expire in <strong>{min}:{sec.toString().padStart(2, '0')}</strong>.
          </p>
        ) : (
          <p className="helper mt-2 m-0" style={{ color: 'rgb(var(--ink-danger))' }}>
            <CircleAlert size={14} className="inline mr-1" /> Codes expired. Try again or fall back
            to manual verification.
          </p>
        )}
        {otpsForDev && (
          <p className="helper mt-2 m-0" style={{ color: 'rgb(var(--ink-accent))' }}>
            <ShieldCheck size={14} className="inline mr-1" /> Dev bypass — codes are prefilled from the server.
          </p>
        )}
      </div>

      <div>
        <label className="label">Code from email</label>
        <OtpInput value={emailOtp} onChange={setEmailOtp} />
      </div>
      <div>
        <label className="label">Code from mobile</label>
        <OtpInput value={mobileOtp} onChange={setMobileOtp} />
      </div>

      {error && (
        <div className="notice notice-danger">
          <CircleAlert size={16} />
          <span>{error}</span>
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          className="btn btn-primary btn-lg"
          onClick={verify}
          disabled={submitting || expired}
        >
          {submitting ? 'Verifying…' : 'Verify and continue'}
        </button>
        <button
          type="button"
          className="btn btn-ghost"
          onClick={onFallbackToPath2}
          disabled={submitting}
        >
          Didn&apos;t receive them? Try manual verification instead
        </button>
      </div>
    </section>
  );
}
